import React, { useState, useEffect, useRef, useId, useMemo } from 'react';
import ReactApexChart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { Box, Typography } from '@mui/material';
import { FONT_COLOR } from '../const/general'
import VerticalSlider from '../item/slider_vertical'
import Progress from '../item/progress'

export default function VerticalBar(props) {
    const [sliderValue, setSliderValue] = useState([0, props.distance]);
    const [categories, setCategories] = useState([]);
    const [allData, setAllData] = useState([]);
    const [riskData, setRiskData] = useState([]);
    const [seriesMax, setSeriesMax] = useState(props.limitVal + 1);
    const [isSlider, setIsSlider] = useState(false);
    const [dataLength, setDataLength] = useState(0);
    const [isNoData, setIsNoData] = useState(false);
    const [areaNameAry, setAreaNameAry] = useState([]);
    const [areaRiverNameAry, setAreaRiverNameAry] = useState([]);
    const [tooltipInfo, setTooltipInfo] = useState(null);

    // チャートごとの一意のキー
    const chartId = useId();
    // 監視用Ref
    const sliderValueRef = useRef(sliderValue);

    // 監視用Ref（ホイール）
    const containerRef = useRef(null);
    const wheelHandlerRef = useRef(null);
    const wheelTimeoutRef = useRef(null);

    const handleWheel = (e) => {
        if (!isSlider || dataLength <= props.distance) return;
        e.preventDefault();
        if (wheelTimeoutRef.current) return;

        const delta = e.deltaY;
        const stepSize = Math.max(1, Math.round(Math.abs(delta) / 50));
        setSliderValue(prev => {
            const maxStart = Math.max(0, dataLength - props.distance);
            let start = prev[0] + (delta > 0 ? -stepSize : stepSize);
            if (start < 0) start = 0;
            if (start > maxStart) start = maxStart;
            return [start, start + props.distance];
        });

        wheelTimeoutRef.current = setTimeout(() => {
            wheelTimeoutRef.current = null;
        }, 10);
    };

    const getLabelValue = (val) => {
        if (val === '-') return '-';
        const num = Number(val);
        if (!isNaN(num)) return String(Math.round(num)); // 小数点なしにする
        return '';
    };

    const getYValue = (val) => {
        if (val === '-') return 0;
        const num = Number(val);
        return !isNaN(num) ? num : 0;
    };

    // チャート情報を格納
    const setChartData = (data) => {
        const isOver = props.distance < data.length
        const { rankData, series_ary, category_ary, tempAreaNameAry, tempAreaRiverNameAry } = props.getRankData(data, isOver, sliderValueRef, getLabelValue, getYValue);

        // データをステイタスに格納
        setAllData(rankData)
        setRiskData(series_ary)
        setCategories(category_ary)
        setIsSlider(isOver)
        setDataLength(rankData.length);
        setAreaNameAry(tempAreaNameAry);
        setAreaRiverNameAry(tempAreaRiverNameAry);

        setSeriesMax(props.limitVal + props.plusValue)
        if (rankData.length > 0) {
            // 最大値の取得
            const max = Math.max(...rankData.map(item => item.max));
            if (max > props.limitVal) {
                setSeriesMax(max + props.plusValue)
            }
        }
    }

    // ローディング終了
    const loadingEnd = () => {
        props.setReady(prev => {
            const updated = { ...prev };
            props.isReadyKey.forEach(key => {
                if (key in updated) {
                    updated[key] = true;
                }
            });
            return updated;
        });
    }

    const series = useMemo(() => {
        return [
            {
                name: '',
                data: riskData
            }
        ];
    }, [riskData]);

    // データ更新が完了したらローディング終了
    useEffect(() => {
        if (allData !== null) {
            const timer = setTimeout(() => {
                loadingEnd();
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [allData]);

    // 定期監視用
    useEffect(() => {
        if (props.allRankData === null ||
            props.allRankData === undefined || 
            props.allRankData.length === 0
        ) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.allRankData && props.allRankData.length > 0) {
            setChartData(props.allRankData)
        }
        props?.setSelectedRoadName?.(null);
        setIsNoData(false)

    // 依存関係の「dateArray」は削除しないでください。
    // これがないとprops.allRankDataに変化がない場合にローディングが消えなくなるので
    }, [props.dateArray, props.allRankData, props.setSelectedRoadName]);

    useEffect(() => {
        if (props.allRankData === null ||
            props.allRankData === undefined || 
            props.allRankData.length === 0
        ) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.allRankData && props.allRankData.length > 0) {
            setChartData(props.allRankData)
        }
        props?.setSelectedRoadName?.(null);
        setIsNoData(false)

        return () => {
            ApexCharts.exec(chartId, 'destroy'); // 明示的に破棄
        };
    }, []);


    // スライダーが移動された場合
    useEffect(() => {
        sliderValueRef.current = sliderValue;
        setChartData(allData)
    }, [sliderValue]);

    useEffect(() => {
        wheelHandlerRef.current = handleWheel;
    }, [handleWheel]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const fn = (e) => {
            if (wheelHandlerRef.current) wheelHandlerRef.current(e);
        };
        el.addEventListener('wheel', fn, { passive: false });
        return () => el.removeEventListener('wheel', fn);
    }, []);

    const options = useMemo(() => ({
        chart: {
            id: chartId,
            type: "bar",
            toolbar: {
                show: false
            },
            events: {
                dataPointSelection: (event, chartContext, config) => {
                    // ローディング開始
                    props.setReady(prev => {
                        const updated = Object.fromEntries(
                            Object.keys(prev).map(key => [key, false])
                        );
                        return updated;
                    });
                    const seriesIndex = config.seriesIndex;
                    const dataPointIndex = config.dataPointIndex;
                    if (seriesIndex === -1 || dataPointIndex === -1) return; // クリック対象がない場合ガード
                    const clickedItem = series[seriesIndex].data[dataPointIndex];
                    props.verticalBarClickEvent(clickedItem)
                },
                dataPointMouseEnter: (event, chartContext, config) => {
                    if (props?.dataPointMouseEnter) {
                        setTooltipInfo(props.dataPointMouseEnter(event, config, series));
                    }                
                },
                mouseLeave: () => {
                    setTooltipInfo(null);
                }
            }
        },
        annotations: props.annotations || {},
        colors: [
            function ({ value, seriesIndex, dataPointIndex, w }) {
                if(props.getRankColor){
                    return props.getRankColor(value, series, seriesIndex, dataPointIndex, w)
                }
            }
        ],
        plotOptions: {
            bar: {
                horizontal: true,
                dataLabels: {
                    position: 'top' // top, center, bottom
                }
            }
        },
        grid: props.grid,
        dataLabels: {
            enabled: true,
            offsetX: 10,
            offsetY: 1,
            style: {
                fontSize: props.fontSize,
                colors: [FONT_COLOR]
            },
            formatter: function (val, opts) {
                return props.getFormat ? props.getFormat(val, opts) : val.toString();
            }
        },
        xaxis: {
            categories: categories,
            axisTicks: {
                show: false
            },
            axisBorder: {
                show: false
            },
            labels: {
                show: false
            },
            min: 0,
            max: seriesMax
        },
        yaxis: {
            axisTicks: {
                show: false
            },
            axisBorder: {
                show: false
            },
            labels: {
                show: true,
                minWidth: 0,
                maxWidth: props.maxWidth,
                style: {
                    colors: FONT_COLOR,
                    fontSize: '10px'
                },
                offsetX: 0,
                offsetY: 0,
                rotate: 0,
                formatter: (value) => {
                    return props.getLabelFormat(value)
                }
            }
        },
        tooltip: {
            enabled: props.enabled,
            x: {
                formatter: function (val, opts) {
                    return props.tooltipFormatX(opts);
                }
            },
            y: {
                formatter: function (val, opts) {
                    return props.tooltipFormatY(val);
                }
            },
            custom: function (opts) {
                return props.tooltipFormatCustom(opts, areaNameAry, areaRiverNameAry);
            }
        },
    }), [categories, seriesMax]);

    // チャートのデータが更新されたら強制再描画
    useEffect(() => {
        if (series && series.length > 0) {
            ApexCharts.exec(chartId,
                "updateSeries", series);
        }
    }, [series]);

    return (
        <>
            <Box
                ref={containerRef}
                sx={{
                    display: 'flex',
                    height: props.height,
                    overflow: 'hidden', // スクロール非表示
                }}
            >
                {/* チャート部分 */}
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    {!props.isReadyFlg ?
                        <Progress />
                        :
                        isNoData ?
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold',
                                    color: 'white'
                                }}
                            >
                                対象データがありません
                            </Typography>
                            :
                            <Box sx={{ position: 'relative', flex: 1, overflow: 'hidden', height: "100%" }}>
                                <ReactApexChart
                                    options={options}
                                    series={series}
                                    type="bar"
                                    height="100%"
                                    width="100%"
                                    style={{ visibility: props.isReadyFlg ? 'visible' : 'hidden' }}
                                />
                                {tooltipInfo?.data && (
                                    props.tooltipContent(tooltipInfo)
                                )}
                            </Box>
                    }
                </Box>

                {/* スライダー部分 */}
                {isSlider &&
                    <Box
                        sx={{
                            width: '20px', // スライダー幅
                            marginLeft: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <VerticalSlider
                            dataLength={dataLength}
                            value={sliderValue}
                            setValue={setSliderValue}
                            distance={props.distance}
                            height={'100%'}
                        />
                    </Box>
                }
            </Box>
        </>
    );
}